import React from 'react';
import { Input, Label, cn } from '@parrot-software/design-system';
import { CircleCheck, CircleX, LoaderCircle } from '@parrot-software/design-system/icons';

/**
 * Form PIN Field Component
 *
 * Reusable PIN input field with label, validation states, and visual feedback.
 * Supports server-side validation states (loading, valid, invalid).
 * Designed to be used with React Hook Form register.
 *
 * @param {FormPinFieldProps} props - Component properties
 * @returns {React.ReactElement} Form PIN field with validation states
 *
 * @example
 * const pinField = register('posPin');
 * const { onChange, ...pinFieldProps } = pinField;
 * const posPinValue = useWatch({ control, name: 'posPin' });
 *
 * <FormPinField
 *   field={pinFieldProps}
 *   onChange={onChange}
 *   value={posPinValue}
 *   label="PIN de acceso"
 *   placeholder="Ingrese PIN"
 *   error={errors.posPin?.message}
 *   disabled={false}
 *   isValidating={isValidatingPin}
 *   serverValidationState={serverPinValid}
 * />
 */

interface FormPinFieldProps {
  field: any;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string | null | undefined;
  label: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  id?: string;
  disabled?: boolean;
  'data-testid'?: string;
  maxLength?: number;
  isValidating?: boolean;
  serverValidationState?: boolean | null;
  labelClassName?: string;
  containerClassName?: string;
}

const styles = {
  container: 'flex flex-col gap-1',
  required: 'text-destructive',
  errorMessage: 'text-xs text-destructive',
  fieldError: 'border-destructive',
  validField: 'ring-1 ring-green-500 border-green-500',
  disabledLabel: 'text-muted-foreground',
  disabledRequired: 'text-muted-foreground',
};

export const FormPinField = ({
  field,
  onChange,
  value,
  label,
  placeholder,
  required = false,
  error,
  id,
  disabled = false,
  'data-testid': dataTestId,
  maxLength = 6,
  isValidating = false,
  serverValidationState = null,
  labelClassName,
  containerClassName,
}: FormPinFieldProps): React.ReactElement => {
  return (
    <div className={cn(styles.container, containerClassName)}>
      <Label className={cn(labelClassName, { [styles.disabledLabel]: disabled })} htmlFor={id}>
        {label} {required && <span className={cn(styles.required, { [styles.disabledRequired]: disabled })}>*</span>}
      </Label>
      <div className="relative">
        <Input
          {...field}
          className={cn(
            'pr-4',
            {
              [styles.fieldError]: !!error || serverValidationState === false,
            },
            serverValidationState === true ? styles.validField : '',
          )}
          data-testid={dataTestId}
          disabled={disabled || isValidating}
          id={id}
          inputMode="numeric"
          maxLength={maxLength}
          onChange={onChange}
          pattern="[0-9]*"
          placeholder={placeholder}
          value={value ?? ''}
        />
        {/* Validation Icons */}
        {isValidating && (
          <LoaderCircle className="absolute right-2 top-[30%] h-4 w-4 text-muted-foreground animate-spin" />
        )}
        {serverValidationState === true && (
          <CircleCheck className="absolute right-2 top-[28%] h-4 w-4 text-green-500" />
        )}
        {serverValidationState === false && <CircleX className="absolute right-2 top-[28%] h-4 w-4 text-red-500" />}
      </div>
      {error && <p className={styles.errorMessage}>{error}</p>}
    </div>
  );
};
